import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../services/supabaseClient";
import {
  getReadingPreferences,
  saveReadingPreferences,
} from "../services/readingPreferences";
import LanguageSelector from "./LanguageSelector";
import { useLanguage } from "../contexts/LanguageContext";
import {
  AuthService,
  PasswordAccountStatus,
  PasswordVerificationCleanupError,
} from "../services/authService";

// Interface for component props
interface SettingsProps {
  userId: string;
  userName: string;
  userEmail: string;
  userTargetLevel: string;
  debugEligible: boolean;
  debugModeEnabled: boolean;
  onDebugModeChange: (enabled: boolean) => void;
  onUpdateSettings: (name: string, targetLevel: string) => void;
  onDeleteAccount: () => Promise<void>;
}

const Settings: React.FC<SettingsProps> = ({
  userId,
  userName,
  userEmail,
  userTargetLevel,
  debugEligible,
  debugModeEnabled,
  onDebugModeChange,
  onUpdateSettings,
  onDeleteAccount,
}) => {
  const { t } = useLanguage();
  const passwordLauncherRef = useRef<HTMLButtonElement>(null);
  const passwordDialogRef = useRef<HTMLDivElement>(null);
  const currentPasswordInputRef = useRef<HTMLInputElement>(null);
  const passwordErrorAlertRef = useRef<HTMLDivElement>(null);
  const passwordDialogWasOpenRef = useRef(false);
  // State for settings management
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [displayNameValidationError, setDisplayNameValidationError] =
    useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [passwordStatus, setPasswordStatus] =
    useState<PasswordAccountStatus | null>(null);
  const [passwordStatusError, setPasswordStatusError] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordCleanupWarning, setPasswordCleanupWarning] = useState("");
  const [passwordChangeBlocked, setPasswordChangeBlocked] = useState(false);

  // Form state
  const [displayName, setDisplayName] = useState(userName);
  const [targetLevel, setTargetLevel] = useState<string>(userTargetLevel);
  const [readingPreferences, setReadingPreferences] = useState({
    ...getReadingPreferences(),
  });

  // Available CEFR levels
  const cefrLevels = ["A1", "A2", "B1", "B2", "C1", "C2"];

  useEffect(() => {
    let cancelled = false;

    AuthService.getPasswordAccountStatus()
      .then((status) => {
        if (!cancelled) setPasswordStatus(status);
      })
      .catch(() => {
        if (!cancelled) setPasswordStatusError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (passwordDialogOpen) {
      passwordDialogWasOpenRef.current = true;
      currentPasswordInputRef.current?.focus();
      return;
    }

    if (passwordDialogWasOpenRef.current) {
      passwordLauncherRef.current?.focus();
      passwordDialogWasOpenRef.current = false;
    }
  }, [passwordDialogOpen]);

  useEffect(() => {
    if (!passwordDialogOpen) return;

    if (changingPassword) {
      passwordDialogRef.current?.focus();
      return;
    }

    if (passwordError) {
      passwordErrorAlertRef.current?.focus();
    }
  }, [changingPassword, passwordDialogOpen, passwordError]);

  // Save settings to Supabase
  const saveSettings = async () => {
    const trimmedDisplayName = displayName.trim();
    setError(null);
    setSuccess(null);
    setDisplayNameValidationError(false);

    if (!trimmedDisplayName) {
      setError(t("settings.displayNameRequired"));
      setDisplayNameValidationError(true);
      return;
    }

    try {
      setLoading(true);

      const { error: updateError } = await supabase
        .from("users")
        .update({
          name: trimmedDisplayName,
          target_tier: targetLevel,
        })
        .eq("id", userId)
        .select();

      if (updateError) {
        throw new Error(
          `Failed to update settings: ${updateError.message || 'Unknown error'}`
        );
      }
    } catch (err) {
      console.error("Error saving settings:", err);
      setError(err instanceof Error ? err.message : "Failed to save settings");
      setLoading(false);
      return;
    }

    setDisplayName(trimmedDisplayName);
    let localRefreshFailed = false;

    try {
      onUpdateSettings(trimmedDisplayName, targetLevel);
    } catch (err) {
      console.error("Error updating local profile after settings save:", err);
      localRefreshFailed = true;
    }

    try {
      saveReadingPreferences(readingPreferences);
    } catch (err) {
      console.error("Error saving local reading preferences after settings save:", err);
      localRefreshFailed = true;
    }

    if (localRefreshFailed) {
      setError(t("settings.localRefreshWarning"));
    } else {
      setSuccess(t("settings.saved"));
      console.log("Settings updated for user:", userId);
    }

    setLoading(false);
  };

  const updateDisplayName = (name: string) => {
    setDisplayName(name);
    if (displayNameValidationError) {
      setDisplayNameValidationError(false);
      setError(null);
    }
  };

  // Handle reading preference change
  const updateReadingPreference = (
    key: keyof typeof readingPreferences,
    value: string
  ) => {
    setReadingPreferences((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const openDeleteDialog = () => {
    setError(null);
    setSuccess(null);
    setDeleteConfirmation("");
    setDeleteError(null);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingAccount) return;
    setDeleteDialogOpen(false);
    setDeleteConfirmation("");
    setDeleteError(null);
  };

  const clearPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setPasswordConfirmation("");
    setPasswordError("");
  };

  const openPasswordDialog = () => {
    setError(null);
    setSuccess(null);
    clearPasswordForm();
    setPasswordDialogOpen(true);
  };

  const closePasswordDialog = () => {
    setPasswordDialogOpen(false);
    clearPasswordForm();
  };

  const handlePasswordDialogKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key === "Escape" && !changingPassword) {
      event.preventDefault();
      closePasswordDialog();
      return;
    }

    if (event.key !== "Tab") return;

    const focusable = Array.from(
      event.currentTarget.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled])'
      )
    );
    const firstFocusable = focusable[0];
    const lastFocusable = focusable[focusable.length - 1];

    if (!firstFocusable || !lastFocusable) {
      event.preventDefault();
      passwordDialogRef.current?.focus();
      return;
    }

    if (document.activeElement === passwordErrorAlertRef.current) {
      event.preventDefault();
      (event.shiftKey ? lastFocusable : firstFocusable).focus();
      return;
    }

    if (event.shiftKey && document.activeElement === firstFocusable) {
      event.preventDefault();
      lastFocusable.focus();
    } else if (!event.shiftKey && document.activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  };

  const handleChangePassword = async (event: React.FormEvent) => {
    event.preventDefault();

    if (newPassword.length < 6) {
      setPasswordError(t("settings.passwordLength"));
      return;
    }
    if (newPassword !== passwordConfirmation) {
      setPasswordError(t("settings.passwordMismatch"));
      return;
    }

    setChangingPassword(true);
    setPasswordError("");
    try {
      await AuthService.changePassword(currentPassword, newPassword);
      closePasswordDialog();
      setPasswordCleanupWarning("");
      setSuccess(t("settings.passwordChanged"));
    } catch (err) {
      if (
        err instanceof PasswordVerificationCleanupError &&
        err.passwordUpdated
      ) {
        closePasswordDialog();
        setSuccess(null);
        setPasswordCleanupWarning(
          t("settings.passwordChangedCleanupWarning")
        );
        setPasswordChangeBlocked(true);
      } else {
        setPasswordError(
          err instanceof Error &&
            err.message.toLowerCase().includes("invalid login credentials")
            ? t("settings.incorrectCurrentPassword")
            : t("settings.changePasswordError")
        );
      }
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDeleteAccount = async (event: React.FormEvent) => {
    event.preventDefault();

    if (deleteConfirmation !== "DELETE") {
      setDeleteError("Type DELETE to confirm account deletion.");
      return;
    }

    try {
      setDeletingAccount(true);
      setError(null);
      setSuccess(null);
      setDeleteError(null);
      await onDeleteAccount();
    } catch (err) {
      console.error("Error deleting account:", err);
      setDeletingAccount(false);
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete account"
      );
    }
  };

  return (
    <div className="mx-auto max-w-4xl p-4 sm:p-6">
      <div inert={passwordDialogOpen}>
      <div className="rounded-lg bg-white p-6 shadow-lg sm:p-8">
        {/* Header */}
        <div className="mb-8">
          <h1
            className="mb-2 text-2xl font-bold text-gray-800 sm:text-3xl"
            tabIndex={-1}
            data-route-focus
          >
            {t("settings.title")}
          </h1>
          <p className="text-gray-600">{t("settings.subtitle")}</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div
            role="status"
            aria-live="polite"
            className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg"
          >
            <div className="text-green-800">{success}</div>
          </div>
        )}

        {passwordCleanupWarning && (
          <div
            role="status"
            aria-live="polite"
            className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4"
          >
            <div className="text-amber-900">{passwordCleanupWarning}</div>
          </div>
        )}

        {error && (
          <div
            id={
              displayNameValidationError
                ? "account-display-name-error"
                : undefined
            }
            role={displayNameValidationError ? "alert" : undefined}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg"
          >
            <div className="text-red-800">{error}</div>
          </div>
        )}

        <div className="space-y-8">
          {/* Display Language */}
          <div className="rounded-lg bg-gray-50 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t("settings.languageTitle")}
            </h3>
            <p className="text-gray-600 mb-4">{t("settings.languageBody")}</p>
            <LanguageSelector />
          </div>

          {/* Target Level */}
          <div className="rounded-lg bg-gray-50 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t("settings.targetTitle")}
            </h3>
            <p className="text-gray-600 mb-4">
              {t("settings.targetBody")}
            </p>
            <select
              value={targetLevel}
              onChange={(e) => setTargetLevel(e.target.value)}
              className="w-full md:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {cefrLevels.map((level) => (
                <option key={level} value={level}>
                  {level} -{" "}
                  {level === "A1"
                    ? "Beginner"
                    : level === "A2"
                    ? "Elementary"
                    : level === "B1"
                    ? "Intermediate"
                    : level === "B2"
                    ? "Upper Intermediate"
                    : level === "C1"
                    ? "Advanced"
                    : "Mastery"}
                </option>
              ))}
            </select>
          </div>

          {debugEligible && (
            <div className="rounded-lg border border-violet-200 bg-violet-50 p-4 sm:p-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-violet-900">
                    {t("settings.debugTitle")}
                  </h3>
                  <p className="mt-1 text-sm text-violet-800">
                    {t("settings.debugBody")}
                  </p>
                  <p className="mt-2 text-xs text-violet-700">
                    {t("settings.debugWarning")}
                  </p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-label={t("settings.debugToggle")}
                  aria-checked={debugModeEnabled}
                  onClick={() => onDebugModeChange(!debugModeEnabled)}
                  className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors ${
                    debugModeEnabled ? "bg-violet-600" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${
                      debugModeEnabled ? "translate-x-6" : "translate-x-1"
                    }`}
                  />
                </button>
              </div>
            </div>
          )}

          {/* Reading Preferences */}
          <div className="rounded-lg bg-gray-50 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t("settings.readingPreferences")}
            </h3>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font
                </label>
                <select
                  value={readingPreferences.fontFamily}
                  onChange={(e) =>
                    updateReadingPreference("fontFamily", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="system">System</option>
                  <option value="serif">Serif</option>
                  <option value="sans">Sans-serif</option>
                  <option value="mono">Monospace</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Font Size
                </label>
                <select
                  value={readingPreferences.fontSize}
                  onChange={(e) =>
                    updateReadingPreference("fontSize", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="small">Small</option>
                  <option value="medium">Medium</option>
                  <option value="large">Large</option>
                  <option value="extra-large">Extra Large</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Theme
                </label>
                <select
                  value={readingPreferences.theme}
                  onChange={(e) =>
                    updateReadingPreference("theme", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                  <option value="sepia">Sepia</option>
                </select>
              </div>
            </div>
          </div>

          {/* Account Actions */}
          <div className="rounded-lg bg-gray-50 p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              {t("settings.account")}
            </h3>
            <div className="space-y-4">
              <div className="grid gap-4 rounded-lg border border-gray-200 bg-white p-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="account-display-name"
                    className="mb-2 block text-sm font-medium text-gray-700"
                  >
                    {t("settings.displayName")}
                  </label>
                  <input
                    id="account-display-name"
                    type="text"
                    value={displayName}
                    onChange={(event) => updateDisplayName(event.target.value)}
                    disabled={loading || deletingAccount}
                    aria-invalid={displayNameValidationError || undefined}
                    aria-describedby={
                      displayNameValidationError
                        ? "account-display-name-error"
                        : undefined
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:bg-gray-100"
                  />
                </div>
                <dl className="min-w-0">
                  <dt className="mb-2 text-sm font-medium text-gray-700">
                    {t("settings.email")}
                  </dt>
                  <dd className="break-all rounded-lg bg-gray-50 px-3 py-2 text-gray-800">
                    {userEmail || t("settings.notAvailable")}
                  </dd>
                </dl>
              </div>
              {passwordStatus?.hasEmailPassword && (
                <button
                  type="button"
                  onClick={openPasswordDialog}
                  ref={passwordLauncherRef}
                  disabled={passwordChangeBlocked}
                  className="w-full rounded-lg border border-blue-200 px-4 py-3 text-left transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <h4 className="font-medium text-blue-800">
                    {t("settings.changePassword")}
                  </h4>
                  <p className="text-sm text-blue-600">
                    {t("settings.changePasswordBody")}
                  </p>
                </button>
              )}
              {passwordStatus && !passwordStatus.hasEmailPassword && (
                <div className="rounded-lg border border-blue-200 px-4 py-3">
                  <h4 className="font-medium text-blue-800">
                    {t("settings.googlePassword")}
                  </h4>
                  <p className="text-sm text-blue-600">
                    {t("settings.googlePasswordBody")}
                  </p>
                </div>
              )}
              {passwordStatusError && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                  {t("settings.passwordStatusError")}
                </p>
              )}
              <button
                type="button"
                onClick={openDeleteDialog}
                disabled={deletingAccount}
                className="w-full text-left px-4 py-3 border border-red-200 rounded-lg hover:bg-red-50 transition-colors disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <h4 className="font-medium text-red-800">{t("settings.deleteAccount")}</h4>
                    <p className="text-sm text-red-600">
                      {t("settings.deleteAccountBody")}
                    </p>
                  </div>
                  <svg
                    className="w-5 h-5 text-red-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="mt-8 pt-6 border-t border-gray-200">
          <button
            onClick={saveSettings}
            disabled={loading || deletingAccount}
            className={`w-full md:w-auto px-6 py-3 rounded-lg font-medium transition-colors ${
              loading || deletingAccount
                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            }`}
          >
            {loading ? t("settings.saving") : t("settings.save")}
          </button>
        </div>

      </div>
      </div>

        {passwordDialogOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            ref={passwordDialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="change-password-title"
            aria-busy={changingPassword}
            tabIndex={-1}
            onKeyDown={handlePasswordDialogKeyDown}
          >
            <form
              onSubmit={handleChangePassword}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <h2
                id="change-password-title"
                className="text-xl font-bold text-gray-800"
              >
                {t("settings.changePassword")}
              </h2>
              <p className="mt-3 text-sm text-gray-700">
                {t("settings.changePasswordBody")}
              </p>
              {passwordError && (
                <div
                  ref={passwordErrorAlertRef}
                  role="alert"
                  tabIndex={-1}
                  className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800"
                >
                  {passwordError}
                </div>
              )}
              <div className="mt-5 space-y-4">
                <div>
                  <label
                    htmlFor="current-password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("settings.currentPassword")}
                  </label>
                  <input
                    id="current-password"
                    ref={currentPasswordInputRef}
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(event) => setCurrentPassword(event.target.value)}
                    disabled={changingPassword}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="new-password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("settings.newPassword")}
                  </label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(event) => setNewPassword(event.target.value)}
                    disabled={changingPassword}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirm-new-password"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("settings.confirmPassword")}
                  </label>
                  <input
                    id="confirm-new-password"
                    type="password"
                    autoComplete="new-password"
                    value={passwordConfirmation}
                    onChange={(event) =>
                      setPasswordConfirmation(event.target.value)
                    }
                    disabled={changingPassword}
                    className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                  />
                </div>
              </div>
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closePasswordDialog}
                  disabled={changingPassword}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("settings.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={
                    changingPassword ||
                    !currentPassword ||
                    !newPassword ||
                    !passwordConfirmation
                  }
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
                >
                  {changingPassword
                    ? t("settings.changingPassword")
                    : t("settings.changePassword")}
                </button>
              </div>
            </form>
          </div>
        )}

        {deleteDialogOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-account-title"
          >
            <form
              onSubmit={handleDeleteAccount}
              className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            >
              <h2
                id="delete-account-title"
                className="text-xl font-bold text-red-800"
              >
                {t("settings.deleteAccount")}
              </h2>
              <p className="mt-3 text-sm text-gray-700">
                {t("settings.deleteDialogBody")}
              </p>
              {deleteError && (
                <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  {deleteError}
                </div>
              )}
              <label
                htmlFor="delete-account-confirmation"
                className="mt-5 block text-sm font-medium text-gray-700"
              >
                {t("settings.typeDelete")}
              </label>
              <input
                id="delete-account-confirmation"
                type="text"
                value={deleteConfirmation}
                onChange={(event) => setDeleteConfirmation(event.target.value)}
                disabled={deletingAccount}
                className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-gray-900 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-100"
                autoComplete="off"
              />
              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeDeleteDialog}
                  disabled={deletingAccount}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {t("settings.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={deletingAccount || deleteConfirmation !== "DELETE"}
                  className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {deletingAccount
                    ? t("settings.deleting")
                    : t("settings.deleteAccount")}
                </button>
              </div>
            </form>
          </div>
        )}
    </div>
  );
};

export default Settings;
